import { Post, Template } from '@repo/editor/src/types';
import {
  AI_TOOLS_PARAM,
  SUB_USE_CASE_PARAM,
  TEMPLATE_PARAM,
  TEMPLATE_SEARCH_QUERY,
  UPLOAD_FILE_PARAM,
  USE_CASE_PARAM,
  NEW_DESIGN_WITH_OBJECT_PARAM,
  NEW_DESIGN_WITH_TEXT_OBJECT_PARAM,
  LIBRARIES_PARAM,
  FOLDER_PARAM,
  NEW_DESIGN_PARAM,
  WORKSPACE_PARAM,
} from '@repo/editor/src/utils/window';
import { buildEditorUrl, buildWebsiteUrl } from '@repo/utils/url';

import { AiArt } from 'global/aiArtApi';

import buildTemplateUrlSearchParams from './buildTemplateUrlSearchParams';

export type TemplateURLParams = {
  id?: Template['id'];
  useCase?: string;
  subUseCase?: string;
  query?: string;
  openTemplateSearchPanel?: boolean;
  category?: string;
  feedIndex?: string;
  featureOrigin?: string;
  isNewProject?: boolean;
  workspaceId?: string;
};

export const TYPEFORM_URL = 'https://kittl.typeform.com';

export const CREATE_NEW_PROJECT_PARAMS = [
  NEW_DESIGN_WITH_OBJECT_PARAM,
  NEW_DESIGN_WITH_TEXT_OBJECT_PARAM,
  TEMPLATE_PARAM,
  USE_CASE_PARAM,
  SUB_USE_CASE_PARAM,
  TEMPLATE_SEARCH_QUERY,
];

export const buildDesignUrl = (id: string): string =>
  buildEditorUrl(`/?did=${id}`);

export const buildNewDesignUrl = (
  options: {
    folderId?: string;
    workspaceId?: string;
  } = {}
): string => {
  const searchParams = new URLSearchParams({
    [NEW_DESIGN_PARAM]: 't',
  });
  if (options.folderId) {
    searchParams.set(FOLDER_PARAM, options.folderId);
  }
  if (options.workspaceId) {
    searchParams.set(WORKSPACE_PARAM, options.workspaceId);
  }
  return buildEditorUrl(`/?${searchParams.toString()}`);
};

export const buildMockupUrl = (
  mockupsGroupId: number,
  mockupId: number
): string => buildEditorUrl(`?mgid=${mockupsGroupId}&mid=${mockupId}`);

export const buildEditorUrlWithTemplateParams = (
  urlParams?: TemplateURLParams
): string => {
  const searchParams = buildTemplateUrlSearchParams(urlParams);

  if (!searchParams) {
    return buildEditorUrl();
  }

  return buildEditorUrl(`/?${searchParams.toString()}`);
};

export const buildAiArtUrl = (objectName: string): string => {
  return buildEditorUrl(`/?ndo=${encodeURIComponent(objectName)}`);
};

export const buildTextUrl = (text: string): string =>
  buildEditorUrl(`/?ndt=${encodeURIComponent(text)}`);

export const buildUpdateTemplateUrl = (id: string): string =>
  buildEditorUrl(`/?utid=${id}`);
export const buildTypeformUrl = (id: string): string =>
  `${TYPEFORM_URL}/to/${id}`;

export const buildUrlWithSearch = (
  query: string,
  fromOnboarding?: boolean
): string => {
  let queryParams = `query=${encodeURIComponent(query)}`;
  if (fromOnboarding) {
    queryParams += `&${FROM_ONBOARDING_QUERY_PARAM}=true`;
  }
  return `/templates?${queryParams}`;
};

export const OPENED_IMAGE_GEN_TOOLBAR_EDITOR_URL = buildEditorUrl(
  `/?${AI_TOOLS_PARAM}=image`
);

export const OPENED_IMAGE_SETS_TOOLBAR_EDITOR_URL = buildEditorUrl(
  `/?${AI_TOOLS_PARAM}=sets`
);

export const OPENED_DESIGN_GEN_TOOLBAR_EDITOR_URL = buildEditorUrl(
  `/?${AI_TOOLS_PARAM}=logo`
);

export const OPENED_BRAND_KITS_TOOLBAR_EDITOR_URL = buildEditorUrl(
  `/?${LIBRARIES_PARAM}=true`
);

export const OPENED_AI_TOOLBAR_EDITOR_URL = buildEditorUrl(
  `/?${AI_TOOLS_PARAM}=true`
);

export const OPENED_UPLOAD_TOOLBAR_EDITOR_URL = buildEditorUrl(
  `/?${UPLOAD_FILE_PARAM}=true`
);

const AI_IMAGE_NAME_LENGTH_FOR_THREE_DOTS = 50;

// Return template category(if any) in a singular form
export const getTemplateCategory = (template: Template): string | null => {
  if (template.groups?.length) {
    let category = template.groups[0].category?.name;
    if (category) {
      // remove s on the end of the category name (e.g. turns T-Shirts into T-Shirt)
      category = category.replace(/s$/, '');
    }
    return category;
  }
  return null;
};

// Return template SEO name
export const getTemplateSEOName = (template: Template): string => {
  const category = getTemplateCategory(template);
  return `${template.name} ${category ? category + ' ' : ''}Design Template`;
};

export const getTemplateSEOObjectName = (
  template?: Template | null
): string | null => {
  if (!template) {
    return null;
  }

  const dirnames = template.templatePreviewObjectName.split('/');
  const basename = dirnames.pop();

  if (!basename) {
    return null;
  }

  const seoName = sanitizePath(getTemplateSEOName(template));

  return `${dirnames.join('/')}/${encodeURIComponent(seoName)}-${basename}`;
};

export const getAiArtSEOName = (aiArt: AiArt): string => {
  return aiArt.name.length >= AI_IMAGE_NAME_LENGTH_FOR_THREE_DOTS
    ? `${aiArt.name}...`
    : `${aiArt.name}`;
};

export const getAiArtSEOObjectName = (aiArt: AiArt): string | undefined => {
  const [dirname, basename] = aiArt.objectName.split('/');

  if (!dirname || !basename) {
    return undefined;
  }

  const seoName = sanitizePath(getAiArtSEOName(aiArt));

  return `api/${dirname}/${encodeURIComponent(seoName)}-${basename}`;
};

const sanitizePath = (path: string): string => {
  return path
    .replace(/[^a-z\d\s-]+/gi, '') // only keep numbers, letters, dashes and spaces
    .replace(/[\s]+/gi, '-') // replace spaces with dashes
    .replace(/-+/gi, '-'); // make sure there is no more than 1 dash in a row
};

/**
 * build the path of a post
 * @param {object} post
 */
export const buildPostPath = (post?: Post): string => {
  if (!post) {
    return '';
  }
  if (!post.template || !post.template.name) {
    return `/design/${post.id}`;
  }

  const category = getTemplateCategory(post.template);
  return `/design/${sanitizePath(post.template.name)}-${
    category ? `${sanitizePath(category)}-` : ''
  }${post.shortId || post.id}`;
};

export const buildAiArtPath = (aiArt?: AiArt): string => {
  if (!aiArt) {
    return '';
  }
  return `/ai-art/${sanitizePath(aiArt.name)}-${
    aiArt.shortId ? aiArt.shortId : aiArt.id
  }`;
};

/**
 * build a link to share a post
 * @param {object} post
 * @param {string} user slug of the current user
 */
export const buildPostShareUrl = (post: Post, user?: string): string => {
  const url = buildWebsiteUrl(buildPostPath(post));

  const creator = post?.template?.createdBy?.slug;
  if (!user || user === creator) {
    return url;
  }
  return `${url}?referral=${user}`;
};

export const SLUG_PREFIX = '@';

export const getDisplaySlug = (slug: string): string => {
  return `${SLUG_PREFIX}${slug}`;
};

export const buildPublicProfilePath = (slug?: string): string => {
  return `/${SLUG_PREFIX}${slug}`;
};

/**
 * the URL path for creating a new team.
 */
export const createTeamPath = '/user/dashboard?createTeam=t';

/**
 * the URL path after joining a team and it's corresponding parameter
 */
export const inviteAcceptedQueryParam = 'inviteAccepted';
export const inviteAcceptedPath = `/user/dashboard?${inviteAcceptedQueryParam}=t`;

/**
 * URL for the Kittl business public Slack channel
 */
export const kittlPublicSlackUrl =
  'https://join.slack.com/t/kittlforbusin-en86680/shared_invite/zt-27s15kxci-GgB_b3DmJYboOkztHFA_XQ';

/**
 * URL parameter signaling that the user came from onboarding to the given page
 */
export const FROM_ONBOARDING_QUERY_PARAM = 'fromOnboarding';

export const CREATE_TEAM = 'createTeam';
export const NEW_PROJECT = 'newProject';

function stripWWW(url: string) {
  return url.replace(/^www./, '');
}

const WHITELISTED_HOSTNAMES = [buildWebsiteUrl(), buildEditorUrl()].map(
  (url) => {
    const hostname = new URL(url).hostname;
    return stripWWW(hostname);
  }
);

export function isRedirectHostnameValid(redirectUrl: string) {
  try {
    const hostname = new URL(redirectUrl).hostname;

    if (!WHITELISTED_HOSTNAMES.includes(stripWWW(hostname))) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
}
